import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import Masonry from '@mui/lab/Masonry';
import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useScope, useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import { selectSeparateTenant } from '@sophocles/features/tenant/slice';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';

import ReportLinkItem from './components/ReportLinkItem';
import ReportSection from './components/ReportSection';
import SiteListing from './components/SiteListing';

function SiteGroupReportsView(props) {
  const {
    match: { params },
  } = props;

  const { t } = useTranslation();
  const { permissions } = useUser();
  const { customer, siteGroup } = useScope();

  const isBillboard = customer?.is_billboard ?? true;
  const canViewInventory = permissions.canSeeInventory;
  const separateTenant = useSelector(selectSeparateTenant);
  const relayLoadTerm = useRelayLoadTerm();

  const baseLink = `/c/${params.cust_id}/plant/${params.sitegroup_id}`;

  return (
    <AppNav notFullHeight page="plant_list" {...props}>
      <Box sx={{ top: '64px', mx: 2, pb: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <TitleBar showBackButton title={t('general.reports')} />

        <Masonry columns={{ xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }} spacing={2} sx={{ m: 0 }}>
          <ReportSection icon={<AssignmentOutlinedIcon />} title={t('reports.standardReports')}>
            <ReportLinkItem to={`${baseLink}/reports/maintenance`} primary={t('reports.alarmMaintenance')} />
            <ReportLinkItem
              to={`${baseLink}/reports/disabledloads`}
              primary={t('reports.disabledReport', { type: relayLoadTerm.plural })}
            />
            {isBillboard ? (
              <>
                <ReportLinkItem to={`${baseLink}/reports/fixture`} primary={t('reports.fixtureReport')} />
                <ReportLinkItem
                  to={`${baseLink}/reports/digitalmaintenance`}
                  primary={t('reports.digitalMaintenance')}
                />
              </>
            ) : null}
            <ReportLinkItem
              to={`${baseLink}/reports/loadschedules`}
              primary={t('reports.illuminationSchedules', { type: relayLoadTerm.singular })}
            />
            <SiteListing isBillboard={isBillboard} siteGroupId={siteGroup?.id} siteGroup={siteGroup} />
          </ReportSection>

          <ReportSection icon={<DashboardOutlinedIcon />} title={t('reports.overviewReports')}>
            <ReportLinkItem to={`${baseLink}/reports/alarmlivesummary`} primary={t('reports.alarmLiveSummary')} />
          </ReportSection>

          {!separateTenant && (
            <ReportSection icon={<WbSunnyOutlinedIcon />} title={t('reports.solarReports')}>
              <ReportLinkItem to={`${baseLink}/reports/dailysolar`} primary={t('reports.dailySolar')} />
              <ReportLinkItem to={`${baseLink}/reports/weeklysolar`} primary={t('reports.weeklySolar')} />
              <ReportLinkItem to={`${baseLink}/reports/monthlysolar`} primary={t('reports.monthlySolar')} />
              <ReportLinkItem to={`${baseLink}/reports/yearlysolar`} primary={t('reports.yearlySolar')} />
            </ReportSection>
          )}

          {canViewInventory && (
            <ReportSection icon={<Inventory2OutlinedIcon />} title={t('reports.inventoryReports')}>
              <ReportLinkItem to={`${baseLink}/inventory`} primary={t('reports.inventoryList')} />
            </ReportSection>
          )}

          <ReportSection icon={<VerifiedOutlinedIcon />} title={t('reports.proofPerformanceReports')}>
            <ReportLinkItem to={`${baseLink}/pop`} primary={t('reports.dailyProofPerformance')} />
          </ReportSection>
        </Masonry>
      </Box>
    </AppNav>
  );
}

export default SiteGroupReportsView;
